import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { MESSAGE } from '@shared/constants';
import { FinancialAccountService } from '@shared/services';
import { ErrorUtils } from '@shared/utils';
import { financialAccountFeature, selectRouteParams } from '@store';
import { exhaustMap, map, catchError, of, delayWhen, timer, tap, mergeMap } from 'rxjs';
import { ToastActions } from '../toast-feature';
import { FinancialAcccountActions } from './financial-account.actions';
import { ModalService } from '@rocketfinancialcorp/rocket-ui/modal';
import { SuccessModalComponent } from '@shared/components';

export const loadFinancialAccounts$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), financialAccountService = inject(FinancialAccountService)) => {
    return actions$.pipe(
      ofType(FinancialAcccountActions.loadFinancialAccounts),
      concatLatestFrom(() => [store.select(financialAccountFeature.selectFinancialAccountsFiltersRequest)]),
      mergeMap(([payload, activeFilters]) => {
        return financialAccountService.getFinancialAccounts({ ...payload.params, activeFilters }).pipe(
          map((financialAccountsList) => {
            return FinancialAcccountActions.loadFinancialAccountsSuccess({
              financialAccounts: financialAccountsList.items,
              totalElements: financialAccountsList.totalElements,
            });
          }),
          catchError((error) => {
            return of(FinancialAcccountActions.loadFinancialAccountsFailure({ error }));
          }),
        );
      }),
    );
  },
  { functional: true },
);

export const loadFinancialAccountsFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(FinancialAcccountActions.loadFinancialAccountsFailure),
      delayWhen(() => timer(400)),
      map(({ error }) => {
        ErrorUtils.catchError('FinancialAcccountActions.loadFinancialAccounts', error);
        return ToastActions.error({ message: 'Unable to fetch Financial Accounts.' });
      }),
    );
  },
  { functional: true },
);

export const loadFinancialAccountDetails$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), financialAccountService = inject(FinancialAccountService)) => {
    return actions$.pipe(
      ofType(FinancialAcccountActions.loadFinancialAccountDetails),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      exhaustMap(([, routeParams]) => {
        return financialAccountService.getFinancialAccountById(routeParams.financialAccountId).pipe(
          map((financialAccount) => {
            return FinancialAcccountActions.loadFinancialAccountDetailsSuccess({ financialAccount });
          }),
          catchError((error) => of(FinancialAcccountActions.loadFinancialAccountDetailsFailure({ error: error.message }))),
        );
      }),
    );
  },
  { functional: true },
);

export const loadFinancialAccountDetailsFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(FinancialAcccountActions.loadFinancialAccountDetailsFailure),
      delayWhen(() => timer(400)),
      map(({ error }) => {
        ErrorUtils.catchError('FinancialAcccountActions.loadFinancialAccountDetails', error);
        return ToastActions.error({ message: MESSAGE.UNKNOWN_ERROR });
      }),
    );
  },
  { functional: true },
);

export const loadFinancialAccountDetailsFailureRedirect$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) => {
    return actions$.pipe(
      ofType(FinancialAcccountActions.loadFinancialAccountDetailsFailure),
      tap({
        next: () => router.navigateByUrl('/app/financial-accounts'),
      }),
    );
  },
  { functional: true, dispatch: false },
);

export const updateFinancialAccountStatus$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    financialAccountService = inject(FinancialAccountService),
    modalService = inject(ModalService),
  ) => {
    return actions$.pipe(
      ofType(FinancialAcccountActions.updateFinancialAccountStatus),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      exhaustMap(([payload, routeParams]) => {
        return financialAccountService.updateFinancialAccountStatus(routeParams.financialAccountId, payload.action).pipe(
          map(() => {
            const modalRef = modalService.open(SuccessModalComponent, { className: 'success-modal' });
            modalRef.componentInstance.actionName = payload.action;
            modalRef.componentInstance.type = 'Financial Account';

            return FinancialAcccountActions.loadFinancialAccountDetails();
          }),
          catchError((error) =>
            of(FinancialAcccountActions.updateFinancialAccountStatusFailure({ error: error.message, action: payload.action })),
          ),
        );
      }),
    );
  },
  { functional: true },
);

export const updateFinancialAccountStatusFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(FinancialAcccountActions.updateFinancialAccountStatusFailure),
      delayWhen(() => timer(400)),
      map(({ error, action }) => {
        ErrorUtils.catchError('FinancialAcccountActions.updateFinancialAccountStatusFailure', error);
        return ToastActions.error({ message: `Unable to ${action} Financial Account.` });
      }),
    );
  },
  { functional: true },
);

export const loadFinancialAccountActivity$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), financialAccountService = inject(FinancialAccountService)) => {
    return actions$.pipe(
      ofType(FinancialAcccountActions.loadFinancialAccountActivity),
      concatLatestFrom(() => [
        store.select(selectRouteParams),
        store.select(financialAccountFeature.selectFinancialAccountActivityFiltersRequest),
      ]),
      mergeMap(([payload, routeParams, activeFilters]) => {
        return financialAccountService
          .getFinancialAccountActivities({ ...payload.params, activeFilters }, routeParams.financialAccountId)
          .pipe(
            map((financialAccountActivity) => {
              return FinancialAcccountActions.loadFinancialAccountActivitySuccess(financialAccountActivity);
            }),
            catchError((error) => {
              return of(FinancialAcccountActions.loadFinancialAccountActivityFailure({ error }));
            }),
          );
      }),
    );
  },
  { functional: true },
);

export const loadFinancialAccountActivityFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(FinancialAcccountActions.loadFinancialAccountActivityFailure),
      delayWhen(() => timer(400)),
      map(({ error }) => {
        ErrorUtils.catchError('FinancialAcccountActions.loadFinancialAccountActivityFailure', error);
        return ToastActions.error({ message: `Unable to load Financial Account Activities.` });
      }),
    );
  },
  { functional: true },
);

export const loadJournalActivity$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), financialAccountService = inject(FinancialAccountService)) => {
    return actions$.pipe(
      ofType(FinancialAcccountActions.loadJournalActivity),
      concatLatestFrom(() => [store.select(selectRouteParams), store.select(financialAccountFeature.selectJournalActivityFiltersRequest)]),
      mergeMap(([payload, routeParams, activeFilters]) => {
        return financialAccountService.getJournalActivity({ ...payload.params, activeFilters }, routeParams.financialAccountId).pipe(
          map((journalActivity) => {
            return FinancialAcccountActions.loadJournalActivitySuccess(journalActivity);
          }),
          catchError((error) => {
            return of(FinancialAcccountActions.loadJournalActivityFailure({ error }));
          }),
        );
      }),
    );
  },
  { functional: true },
);

export const loadJournalActivityFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(FinancialAcccountActions.loadJournalActivityFailure),
      delayWhen(() => timer(400)),
      map(({ error }) => {
        ErrorUtils.catchError('FinancialAcccountActions.loadJournalActivityFailure', error);
        return ToastActions.error({ message: `Unable to load Journal Activities.` });
      }),
    );
  },
  { functional: true },
);

export const loadFinancialAccountLimits$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), financialAccountService = inject(FinancialAccountService)) => {
    return actions$.pipe(
      ofType(FinancialAcccountActions.loadFinancialAccountDetails, FinancialAcccountActions.loadFinancialAccountLimits),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      exhaustMap(([, routeParams]) => {
        return financialAccountService.getFinancialAccountLimits({ financialAccountId: routeParams.financialAccountId }).pipe(
          map((financialAccountLimits) => {
            return FinancialAcccountActions.loadFinancialAccountLimitsSuccess({ financialAccountLimits });
          }),
          catchError((error) => of(FinancialAcccountActions.loadFinancialAccountLimitsFailure({ error: error.message }))),
        );
      }),
    );
  },
  { functional: true },
);
